import { useState, useMemo, useEffect } from 'react';
import { X, Plus, Save, Edit2, Trash2, RefreshCw, Shield, ExternalLink, Link2, Calendar, Check } from 'lucide-react';
import DateField from './DateField.jsx';
import {
  createAppointment as apiCreateAppointment,
  updateAppointment as apiUpdateAppointment,
  deleteAppointment as apiDeleteAppointment,
  generateRenewalReminders as apiGenerateRenewalReminders,
  listAppointments as apiListAppointments,
  getExternalCalStatus as apiGetExternalCalStatus,
  connectExternalCal as apiConnectExternalCal,
  syncExternalCal as apiSyncExternalCal,
  disconnectExternalCal as apiDisconnectExternalCal,
  getCalendarSyncStatus as apiGetCalendarSyncStatus,
  connectMicrosoftCalendarSync as apiConnectMsCalSync,
  connectGoogleCalendarSync as apiConnectGoogleCalSync,
  toggleCalendarSync as apiToggleCalSync,
  syncCalendarPush as apiSyncCalPush,
  disconnectCalendarSync as apiDisconnectCalSync,
} from '../api.js';
import { toast } from './Toast.jsx';
import { fmt } from '../utils.js';
import { esCuentaDeDosAgentes } from '../rolesDeLaCuenta.js';
import { confirmDialog, ClientSearchSelect, NoteEditor, RichNote } from './ui.jsx';
import { LINEAS_DE_BLOQUEO, nombreDeLinea } from '../lineasDeBloqueo.js';
import { textoDeErrorDelCalendario } from '../errorDelCalendario.js';

// Hoisted to module scope so its edit state survives parent re-renders. Nested
// inside CalendarView it remounted (wiping the half-typed event) on any parent
// re-render — e.g. an SSE appointment update arriving while the form is open.
// (feedback_setstate_updater_side_effects)

function chipStyle(activo) {
  return {
    fontSize: ".68rem", fontWeight: 700, padding: ".25rem .6rem", borderRadius: 100, cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${activo ? "var(--c-cyan,#00d4ff)" : "var(--c-border,#1a3550)"}`,
    background: activo ? "rgba(0,212,255,.14)" : "transparent",
    color: activo ? "var(--c-cyan,#00d4ff)" : "var(--c-text2,#7aa3c0)",
  };
}

// "BLOQUEA LA HORA PARA" (Lissette 2026-10-11): en un equipo, la cita de salud
// de las 10 no tiene por qué cerrarle esa hora a quien lleva Medicare. Se elige
// para qué líneas y, si hace falta, para qué agentes. Sin elegir nada = todas
// las líneas y solo quien la crea, que es lo que pasaba siempre.
function AlcanceDelBloqueo({ f, set, users, lang }) {
  const es = lang === "es";
  const lineas = Array.isArray(f.bloqueaLineas) ? f.bloqueaLineas : [];
  const agentes = Array.isArray(f.bloqueaAgentes) ? f.bloqueaAgentes : [];
  const conCuenta = (users || []).filter((u) => u && u.id && u.isActive !== false);
  const alternar = (lista, id) => (lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);
  const lbl = { fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 };
  return (
    <div style={{ marginBottom: ".75rem", padding: ".6rem .7rem", border: "1px dashed var(--c-border,#1a3550)", borderRadius: 10 }}>
      <label style={lbl}>🔒 {es ? "Bloquea la hora para" : "Blocks the time for"}</label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: ".5rem" }}>
        <button type="button" onClick={() => set("bloqueaLineas", [])} style={chipStyle(lineas.length === 0)}>
          {es ? "Todas las líneas" : "All lines"}
        </button>
        {LINEAS_DE_BLOQUEO.map((l) => (
          <button key={l.id} type="button" onClick={() => set("bloqueaLineas", alternar(lineas, l.id))} style={chipStyle(lineas.includes(l.id))}>
            {es ? l.es : l.en}
          </button>
        ))}
      </div>
      {conCuenta.length > 1 && <>
        <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", marginBottom: 4 }}>
          {es ? "¿Para qué agentes? (sin elegir = solo para ti)" : "For which agents? (none = only you)"}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {conCuenta.map((u) => (
            <button key={u.id} type="button" onClick={() => set("bloqueaAgentes", alternar(agentes, u.id))} style={chipStyle(agentes.includes(u.id))}>
              {u.name || u.email}
            </button>
          ))}
        </div>
      </>}
    </div>
  );
}

// "👤 ATIENDE" (Lissette 2026-10-17): pasar la cita a otra persona de la
// agencia. Lo puede hacer cualquiera que edite el calendario (eligió la opción
// 1). Solo en citas hechas a mano o reservadas por el enlace: las demás tienen
// dueño por otro lado (ver deskte-api/src/utils/reasignarCita.ts).
export function sePuedePasarLaCita(ev) {
  return !!ev && !!ev.agentId && (ev.source == null || ev.source === "public_booking");
}

function QuienAtiende({ f, set, users, lang }) {
  const es = lang === "es";
  const gente = (users || []).filter((u) => u && u.id && u.isActive !== false);
  if (!sePuedePasarLaCita(f) || gente.length < 2) return null;
  const opciones = gente.some((u) => u.id === f.agentId) ? gente : [{ id: f.agentId, name: f.agentName || (es ? "Quien la tiene ahora" : "Current owner") }, ...gente];
  return (
    <div style={{ marginBottom: ".65rem" }}>
      <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>👤 {es ? "Atiende" : "Handled by"}</label>
      <select className="dkt-inp" value={f.agentId} onChange={(e) => set("agentId", e.target.value)} style={{ width: "100%" }}>
        {opciones.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
      </select>
      <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", marginTop: 3 }}>
        {es ? "Al guardar, la cita pasa a la agenda de esa persona y le llega un aviso." : "On save, the appointment moves to that person's calendar and they get a notice."}
      </div>
    </div>
  );
}

/** "Salud · Medicare — Josef, Nina": lo que cierra un evento, en una línea. */
export function resumenDelBloqueo(ev, users, lang) {
  const es = lang === "es";
  const lineas = (ev?.bloqueaLineas || []).map((id) => nombreDeLinea(id, es));
  const agentes = (ev?.bloqueaAgentes || []).map((id) => (users || []).find((u) => u.id === id)?.name).filter(Boolean);
  if (!lineas.length && !agentes.length) return "";
  return [lineas.join(" · "), agentes.join(", ")].filter(Boolean).join(" — ");
}

// Duraciones que se ofrecen; la guardada se agrega si no está en la lista.
const DURACIONES = [15, 30, 45, 60, 90, 120, 180, 240, 480];
export function opcionesDeDuracion(actual) {
  const n = Number(actual) || 60;
  return DURACIONES.includes(n) ? DURACIONES : [...DURACIONES, n].sort((a, b) => a - b);
}
export function textoDeDuracion(m, lang) {
  const es = lang === "es";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h} h ${r} min` : (h === 1 ? (es ? "1 hora" : "1 hour") : (es ? `${h} horas` : `${h} hours`));
}
/** "11:00" + 90 min → "12:30 PM" (formato en-US, como el resto de fechas). */
export function horaDeFin(hhmm, minutos) {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  const total = (h * 60 + m + Number(minutos || 0)) % (24 * 60);
  const d = new Date(2000, 0, 1, Math.floor(total / 60), total % 60);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function EventForm({ initial, onSave, onCancel, isEdit, lang, emptyEvent, isAgencyOwner, clients, EVENT_TYPES, users }) {
    const [f,setF]=useState(initial||emptyEvent);
    const set=(k,v)=>setF(p=>({...p,[k]:v}));
    return(
      <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.85)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
        <div style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:14,padding:"1.5rem",maxWidth:460,width:"100%"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1rem"}}>
            <div style={{fontWeight:800,fontSize:"1rem",color:"var(--c-white,#eef6ff)"}}>📅 {isEdit?(lang==="es"?"Editar Evento":"Edit Event"):(lang==="es"?"Nuevo Evento":"New Event")}</div>
            <button type="button" onClick={onCancel} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)"}}><X size={18}/></button>
          </div>
          <div style={{marginBottom:".65rem"}}>
            <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:4}}>{lang==="es"?"Título":"Title"}</label>
            <input className="dkt-inp" value={f.title} onChange={e=>set("title",e.target.value)} placeholder={lang==="es"?"Cita con cliente...":"Client appointment..."} style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".65rem",marginBottom:".65rem"}}>
            <div>
              <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:4}}>{lang==="es"?"Fecha":"Date"}</label>
              <DateField className="dkt-inp" value={f.date} onChange={e=>set("date",e.target.value)} style={{width:"100%",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:4}}>{lang==="es"?"Hora":"Time"}</label>
              <input type="time" className="dkt-inp" value={f.time||""} onChange={e=>set("time",e.target.value)} style={{width:"100%",boxSizing:"border-box"}}/>
            </div>
          </div>
          {/* DURACIÓN (Lissette 2026-10-17: "no me deja colocar cuánto tiempo
              será la reunión"). Cierra la hora en el Booking Link solo ese rato. */}
          <div style={{marginBottom:".65rem"}}>
            <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:4}}>{lang==="es"?"Duración":"Duration"}</label>
            <div style={{display:"flex",alignItems:"center",gap:".5rem"}}>
              <select className="dkt-inp" value={String(f.durationMinutes||60)} onChange={e=>set("durationMinutes",Number(e.target.value))} style={{flex:1}}>
                {opcionesDeDuracion(f.durationMinutes).map(m=><option key={m} value={String(m)}>{textoDeDuracion(m, lang)}</option>)}
              </select>
              {f.time && <span style={{fontSize:".72rem",color:"var(--c-text2,#7aa3c0)",whiteSpace:"nowrap"}}>{lang==="es"?"hasta las":"until"} {horaDeFin(f.time, f.durationMinutes||60, lang)}</span>}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".65rem",marginBottom:".65rem"}}>
            <div>
              <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:4}}>{lang==="es"?"Tipo":"Type"}</label>
              <select className="dkt-inp" value={f.type} onChange={e=>set("type",e.target.value)} style={{width:"100%"}}>
                {EVENT_TYPES.map(et=><option key={et.id} value={et.id}>{et.icon} {et.label}</option>)}
              </select>
            </div>
            {!isAgencyOwner && <div>
              <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:4}}>{lang==="es"?"Cliente":"Client"}</label>
              <ClientSearchSelect clients={clients} value={f.clientId} onChange={e=>set("clientId",e.target.value)} lang={lang} noneLabel={`-- ${lang==="es"?"Opcional":"Optional"} --`} />
            </div>}
          </div>
          {isEdit && <QuienAtiende f={f} set={set} users={users} lang={lang}/>}
          <AlcanceDelBloqueo f={f} set={set} users={users} lang={lang}/>
          <div style={{marginBottom:".75rem"}}>
            <label style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:4}}>{lang==="es"?"Notas":"Notes"}</label>
            <NoteEditor lang={lang==="es"?'es':'en'} rows={2} value={f.notes||""} onChange={v=>set("notes",v)} style={{width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"flex",gap:".5rem",justifyContent:"flex-end"}}>
            <button type="button" onClick={onCancel} className="dkt-btn-s">{lang==="es"?"Cancelar":"Cancel"}</button>
            <button type="button" onClick={()=>onSave(f)} disabled={!f.title||!f.date}
              style={{display:"flex",alignItems:"center",gap:5,background:(!f.title||!f.date)?"#2a4560":"linear-gradient(135deg,#0099cc,#0055aa)",border:"none",color:"#fff",fontSize:".78rem",fontWeight:700,padding:".5rem 1.2rem",borderRadius:9,cursor:(!f.title||!f.date)?"not-allowed":"pointer",fontFamily:"inherit"}}>
              <Save size={13}/>{lang==="es"?"Guardar":"Save"}
            </button>
          </div>
        </div>
      </div>
    );
}

// Connect-my-personal-calendar modal. Hoisted to module scope (like EventForm)
// so its input state survives parent re-renders (e.g. an SSE refresh). Lets the
// agent paste their SECRET Google/Outlook iCal URL; the backend mirrors events
// as "Busy" blocks so seats don't double-book. One-way, read-only.
function ExternalCalModal({ lang, onClose, onSynced }) {
  const [status, setStatus] = useState(null); // { connected, provider, syncedAt, error }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [provider, setProvider] = useState('google');

  useEffect(() => {
    let alive = true;
    apiGetExternalCalStatus()
      .then((s) => { if (alive) { setStatus(s); if (s.provider) setProvider(s.provider); } })
      .catch(() => { if (alive) setStatus({ connected: false }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const fmtSynced = (iso) => {
    if (!iso) return lang === 'es' ? 'nunca' : 'never';
    try { return new Date(iso).toLocaleString(lang === 'es' ? 'es-US' : 'en-US'); } catch { return iso; }
  };

  const doConnect = async () => {
    if (!url.trim()) { toast.warn(lang === 'es' ? 'Pega el enlace de tu calendario.' : 'Paste your calendar link.'); return; }
    setBusy(true);
    try {
      const res = await apiConnectExternalCal(url.trim(), provider);
      setStatus({ connected: true, provider: res.provider, syncedAt: res.syncedAt, error: res.error });
      setUrl('');
      if (res.error) {
        toast.error((lang === 'es' ? 'Conectado, pero la sincronización falló: ' : 'Connected, but sync failed: ') + res.error);
      } else {
        toast.success(lang === 'es'
          ? `Calendario conectado — ${res.eventsImported ?? 0} eventos importados.`
          : `Calendar connected — ${res.eventsImported ?? 0} events imported.`);
        onSynced && onSynced();
      }
    } catch (e) {
      toast.error((lang === 'es' ? 'No se pudo conectar: ' : 'Could not connect: ') + e.message);
    } finally { setBusy(false); }
  };

  const doSync = async () => {
    setBusy(true);
    try {
      const res = await apiSyncExternalCal();
      setStatus((p) => ({ ...(p || {}), connected: true, syncedAt: res.syncedAt, error: res.error }));
      if (res.error) toast.error((lang === 'es' ? 'Sincronización falló: ' : 'Sync failed: ') + res.error);
      else { toast.success(lang === 'es' ? `Sincronizado — ${res.eventsImported ?? 0} eventos.` : `Synced — ${res.eventsImported ?? 0} events.`); onSynced && onSynced(); }
    } catch (e) {
      toast.error((lang === 'es' ? 'No se pudo sincronizar: ' : 'Could not sync: ') + e.message);
    } finally { setBusy(false); }
  };

  const doDisconnect = async () => {
    if (!(await confirmDialog({ message: lang === 'es' ? '¿Desconectar tu calendario personal? Se quitarán los bloques "Ocupado".' : 'Disconnect your personal calendar? The "Busy" blocks will be removed.', danger: true }))) return;
    setBusy(true);
    try {
      await apiDisconnectExternalCal();
      setStatus({ connected: false, provider: null, syncedAt: null, error: null });
      toast.success(lang === 'es' ? 'Calendario desconectado.' : 'Calendar disconnected.');
      onSynced && onSynced();
    } catch (e) {
      toast.error((lang === 'es' ? 'No se pudo desconectar: ' : 'Could not disconnect: ') + e.message);
    } finally { setBusy(false); }
  };

  const lbl = { fontSize: '.65rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 4 };
  const help = provider === 'outlook'
    ? (lang === 'es'
        ? 'En Outlook: Configuración → Calendario → Calendarios compartidos → "Publicar un calendario" → elige el calendario → Publicar → copia el enlace ICS.'
        : 'In Outlook: Settings → Calendar → Shared calendars → "Publish a calendar" → pick the calendar → Publish → copy the ICS link.')
    : (lang === 'es'
        ? 'En Google Calendar: Configuración → "Configuración de mis calendarios" → tu calendario → "Integrar calendario" → copia la "Dirección secreta en formato iCal".'
        : 'In Google Calendar: Settings → "Settings for my calendars" → your calendar → "Integrate calendar" → copy the "Secret address in iCal format".');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1.5rem', maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: '.45rem' }}>
            <Calendar size={18} />{lang === 'es' ? 'Conectar mi calendario personal' : 'Connect my personal calendar'}
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)' }}><X size={18} /></button>
        </div>

        <div style={{ fontWeight: 800, fontSize: '.9rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: '.45rem', marginBottom: '.4rem' }}>
          <Link2 size={15} />{lang === 'es' ? 'Traer mi calendario a Deskte ("Ocupado")' : 'Bring my calendar into Deskte ("Busy")'}
        </div>
        <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5, marginBottom: '1rem' }}>
          {lang === 'es'
            ? 'Trae tus eventos personales de Google u Outlook a Deskte como bloques "Ocupado", para que quien agende por ti no te ponga citas encima. Es de una sola vía y solo lectura — Deskte nunca cambia tu calendario.'
            : 'Bring your personal Google/Outlook events into Deskte as "Busy" blocks so whoever schedules for you won\'t double-book. One-way and read-only — Deskte never changes your calendar.'}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{lang === 'es' ? 'Cargando…' : 'Loading…'}</div>
        ) : status?.connected ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', background: 'rgba(16,185,129,.1)', border: '1px solid rgba(16,185,129,.35)', borderRadius: 9, padding: '.6rem .8rem', marginBottom: '.75rem' }}>
              <Check size={16} style={{ color: '#10b981' }} />
              <div style={{ fontSize: '.78rem', color: 'var(--c-white,#eef6ff)', fontWeight: 600 }}>
                {lang === 'es' ? 'Conectado' : 'Connected'} {status.provider ? `(${status.provider === 'google' ? 'Google' : status.provider === 'outlook' ? 'Outlook' : status.provider})` : ''}
              </div>
            </div>
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
              {lang === 'es' ? 'Última sincronización: ' : 'Last sync: '}{fmtSynced(status.syncedAt)}
            </div>
            {status.error && (
              <div style={{ fontSize: '.72rem', color: '#fca5a5', background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '.5rem .7rem', marginBottom: '.75rem' }}>
                ⚠️ {status.error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '.5rem', marginTop: '.75rem' }}>
              <button type="button" onClick={doSync} disabled={busy}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', fontSize: '.76rem', fontWeight: 700, padding: '.5rem 1rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: busy ? .6 : 1 }}>
                <RefreshCw size={13} />{lang === 'es' ? 'Sincronizar ahora' : 'Sync now'}
              </button>
              <button type="button" onClick={doDisconnect} disabled={busy}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid rgba(239,68,68,.4)', color: '#fca5a5', fontSize: '.76rem', fontWeight: 700, padding: '.5rem 1rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                <Trash2 size={13} />{lang === 'es' ? 'Desconectar' : 'Disconnect'}
              </button>
            </div>
            <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: '.85rem', lineHeight: 1.5, paddingTop: '.75rem', borderTop: '1px solid var(--c-border,#1a3550)' }}>
              {lang === 'es'
                ? 'Tu equipo verá estos eventos como "Ocupado". Para que tu asistente vea el título real, dale el permiso "Ver detalles de mi calendario personal" en su configuración de Asiento.'
                : 'Your team sees these as "Busy". To let your assistant see the real title, grant them the "See my personal calendar details" permission in their Seat settings.'}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '.65rem' }}>
              <label style={lbl}>{lang === 'es' ? 'Proveedor' : 'Provider'}</label>
              <select className="dkt-inp" value={provider} onChange={(e) => setProvider(e.target.value)} style={{ width: '100%' }}>
                <option value="google">Google Calendar</option>
                <option value="outlook">Outlook</option>
                <option value="other">{lang === 'es' ? 'Otro (enlace iCal)' : 'Other (iCal link)'}</option>
              </select>
            </div>
            <div style={{ marginBottom: '.65rem' }}>
              <label style={lbl}>{lang === 'es' ? 'Enlace secreto (iCal / ICS)' : 'Secret link (iCal / ICS)'}</label>
              <input className="dkt-inp" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://calendar.google.com/calendar/ical/.../basic.ics" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.5, background: 'var(--c-surface2,#112538)', borderRadius: 8, padding: '.6rem .75rem', marginBottom: '.85rem' }}>
              💡 {help}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={doConnect} disabled={busy || !url.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: (busy || !url.trim()) ? '#2a4560' : 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', fontSize: '.78rem', fontWeight: 700, padding: '.5rem 1.2rem', borderRadius: 9, cursor: (busy || !url.trim()) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                <Link2 size={14} />{busy ? (lang === 'es' ? 'Conectando…' : 'Connecting…') : (lang === 'es' ? 'Conectar' : 'Connect')}
              </button>
            </div>
          </div>
        )}

        <TwoWaySyncSection lang={lang} />
      </div>
    </div>
  );
}

// Two-way sync (Option B): push Deskte appointments OUT to Google/Outlook.
// Separate connection (OAuth, calendar write scope) from the read-only iCal
// import above. Outlook is active; Google is gated on Google app verification.
function TwoWaySyncSection({ lang }) {
  const [st, setSt] = useState(null); // { connected, integration, microsoftConfigured, googleCalendarEnabled }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = () => apiGetCalendarSyncStatus()
    .then((s) => setSt(s))
    .catch(() => setSt({ connected: false }))
    .finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  // Listen for the OAuth popup posting back its result (kind:'calendar').
  useEffect(() => {
    function onMsg(ev) {
      if (ev.origin !== window.location.origin) return;
      if (!ev?.data || ev.data.type !== 'deskte-oauth-result' || ev.data.kind !== 'calendar') return;
      setBusy(false);
      if (ev.data.status === 'success') {
        toast.success(lang === 'es' ? 'Calendario conectado para sincronización de dos vías.' : 'Calendar connected for two-way sync.');
        refresh();
      } else {
        toast.error((lang === 'es' ? 'No se pudo conectar: ' : 'Could not connect: ') + textoDeErrorDelCalendario(ev.data.error, lang));
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [lang]);

  const openOAuth = (authUrl) => {
    const popup = window.open(authUrl, 'deskte-cal-oauth', 'width=560,height=720,menubar=no,toolbar=no');
    if (!popup) window.location.href = authUrl; // popup blocked → full redirect
  };

  const connectOutlook = async () => {
    setBusy(true);
    try { const { authUrl } = await apiConnectMsCalSync('consumers'); openOAuth(authUrl); }
    catch (e) { toast.error(e.message); setBusy(false); }
  };
  const connectGoogle = async () => {
    setBusy(true);
    try { const { authUrl } = await apiConnectGoogleCalSync(); openOAuth(authUrl); }
    catch (e) { toast.error(e.message); setBusy(false); }
  };
  const doSyncNow = async () => {
    setBusy(true);
    try {
      const res = await apiSyncCalPush();
      toast.success(lang === 'es'
        ? `Sincronizado: ${res.pushed ?? 0} citas enviadas · ${res.pulled ?? 0} eventos traídos.`
        : `Synced: ${res.pushed ?? 0} pushed · ${res.pulled ?? 0} pulled.`);
      refresh();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const toggle = async (enabled) => {
    setBusy(true);
    try { await apiToggleCalSync(enabled); await refresh(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!(await confirmDialog({ message: lang === 'es' ? '¿Desconectar la sincronización de dos vías? Las citas ya creadas se quedan en tu calendario.' : 'Disconnect two-way sync? Already-created events stay in your calendar.', danger: true }))) return;
    setBusy(true);
    try { await apiDisconnectCalSync(); await refresh(); toast.success(lang === 'es' ? 'Desconectado.' : 'Disconnected.'); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const integ = st?.integration;
  const provLabel = integ?.provider === 'GOOGLE' ? 'Google Calendar' : 'Outlook';
  const fmtD = (iso) => { if (!iso) return lang === 'es' ? 'nunca' : 'never'; try { return new Date(iso).toLocaleString(lang === 'es' ? 'es-US' : 'en-US'); } catch { return iso; } };

  return (
    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid var(--c-border,#1a3550)' }}>
      <div style={{ fontWeight: 800, fontSize: '.9rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: '.45rem', marginBottom: '.4rem' }}>
        <RefreshCw size={15} />{lang === 'es' ? 'Enviar mis citas a mi calendario (dos vías)' : 'Send my appointments to my calendar (two-way)'}
      </div>
      <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5, marginBottom: '.75rem' }}>
        {lang === 'es'
          ? 'Conecta tu cuenta una vez y funciona en AMBOS sentidos: las citas que creas en Deskte aparecen en tu Google/Outlook, y tus eventos personales aparecen en Deskte como "Ocupado". No necesitas el enlace secreto.'
          : 'Connect once and it works BOTH ways: appointments you create in Deskte appear in your Google/Outlook, and your personal events appear in Deskte as "Busy". No secret link needed.'}
      </div>

      {loading ? (
        <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.78rem' }}>{lang === 'es' ? 'Cargando…' : 'Loading…'}</div>
      ) : st?.connected ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', background: 'rgba(16,185,129,.1)', border: '1px solid rgba(16,185,129,.35)', borderRadius: 9, padding: '.6rem .8rem', marginBottom: '.5rem' }}>
            <Check size={16} style={{ color: '#10b981' }} />
            <div style={{ fontSize: '.78rem', color: 'var(--c-white,#eef6ff)', fontWeight: 600 }}>
              {provLabel}{integ?.email ? ` — ${integ.email}` : ''}
            </div>
          </div>
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
            {lang === 'es' ? 'Último envío: ' : 'Last push: '}{fmtD(integ?.lastSyncedAt)}
            {integ && integ.syncEnabled === false && <span style={{ color: '#fbbf24', marginLeft: 8 }}>{lang === 'es' ? '(pausado)' : '(paused)'}</span>}
          </div>
          {integ?.lastError && (
            <div style={{ fontSize: '.72rem', color: '#fca5a5', background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '.5rem .7rem', marginBottom: '.5rem' }}>⚠️ {textoDeErrorDelCalendario(integ.lastError, lang)}</div>
          )}
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={doSyncNow} disabled={busy}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', fontSize: '.74rem', fontWeight: 700, padding: '.45rem .9rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: busy ? .6 : 1 }}>
              <RefreshCw size={12} />{lang === 'es' ? 'Sincronizar ahora' : 'Sync now'}
            </button>
            <button type="button" onClick={() => toggle(!(integ?.syncEnabled !== false))} disabled={busy}
              style={{ background: 'none', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.74rem', fontWeight: 700, padding: '.45rem .9rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              {integ?.syncEnabled === false ? (lang === 'es' ? 'Reanudar' : 'Resume') : (lang === 'es' ? 'Pausar' : 'Pause')}
            </button>
            <button type="button" onClick={disconnect} disabled={busy}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid rgba(239,68,68,.4)', color: '#fca5a5', fontSize: '.74rem', fontWeight: 700, padding: '.45rem .9rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              <Trash2 size={12} />{lang === 'es' ? 'Desconectar' : 'Disconnect'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={connectOutlook} disabled={busy || !st?.microsoftConfigured}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: (busy || !st?.microsoftConfigured) ? '#2a4560' : 'linear-gradient(135deg,#0078d4,#005a9e)', border: 'none', color: '#fff', fontSize: '.78rem', fontWeight: 700, padding: '.5rem 1.1rem', borderRadius: 9, cursor: (busy || !st?.microsoftConfigured) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            <Link2 size={14} />{busy ? (lang === 'es' ? 'Abriendo…' : 'Opening…') : (lang === 'es' ? 'Conectar Outlook' : 'Connect Outlook')}
          </button>
          {st?.googleCalendarEnabled ? (
            <button type="button" onClick={connectGoogle} disabled={busy}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#ea4335,#c5221f)', border: 'none', color: '#fff', fontSize: '.78rem', fontWeight: 700, padding: '.5rem 1.1rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              <Link2 size={14} />{lang === 'es' ? 'Conectar Google' : 'Connect Google'}
            </button>
          ) : (
            <span title={lang === 'es' ? 'Pendiente de aprobación de Google' : 'Pending Google approval'}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', color: 'var(--c-text3,#45688a)', fontSize: '.74rem', fontWeight: 700, padding: '.5rem 1.1rem', borderRadius: 9 }}>
              Google {lang === 'es' ? '(próximamente)' : '(coming soon)'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function CalendarView({ events, onEventsChange, clients, policies = [], users, currentUser, lang }) {
  // Open the client detail page for the policy attached to a renewal event.
  const openPolicyClient = (ev) => {
    if (!ev?.policyId) return;
    const pol = policies.find(p => p.id === ev.policyId);
    if (!pol) { toast.warn(lang === 'es' ? 'Póliza no encontrada' : 'Policy not found'); return; }
    const cl = clients.find(c => c.id === pol.clientId);
    if (!cl) { toast.warn(lang === 'es' ? 'Cliente no encontrado' : 'Client not found'); return; }
    if (typeof window !== 'undefined') {
      window.__magGoToClient = cl;
      window.dispatchEvent(new CustomEvent('deskte:navigate', { detail: { view: 'clients' } }));
    }
  };
  // Compute days-until-expiration for a policy (renewal events use this badge).
  const daysUntilExp = (policyId) => {
    const pol = policies.find(p => p.id === policyId);
    if (!pol?.expirationDate) return null;
    const exp = new Date(pol.expirationDate + 'T00:00:00');
    if (isNaN(exp.getTime())) return null;
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    return Math.round((exp.getTime() - todayMid.getTime()) / 86400000);
  };
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  // VISTA MES / SEMANA / DÍA (Lissette 2026-11-03: "la opción de poner
  // mensual o diario o semana porque se ve muy chiquito todo"). Es de quien
  // mira: se recuerda en este navegador.
  const [viewMode, setViewModeRaw] = useState(() => {
    try { const v = localStorage.getItem('deskte.calendario.vista'); return ["month","week","day"].includes(v) ? v : "month"; }
    catch { return "month"; }
  });
  const setViewMode = (v) => {
    setViewModeRaw(v);
    try { localStorage.setItem('deskte.calendario.vista', v); } catch { /* sin almacenamiento: vale solo esta visita */ }
  };
  // El día de referencia de las vistas Semana y Día (texto YYYY-MM-DD, día local).
  const [ancla, setAnclaRaw] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,"0")}-${String(t.getDate()).padStart(2,"0")}`;
  });
  const [showExtCal, setShowExtCal] = useState(false); // "Connect my calendar" modal
  // Sidebar click on "Calendar" closes any open form / detail.
  useEffect(()=>{
    const h=(e)=>{ if(e?.detail?.view==="calendar"){ setSelectedDate(null); setShowForm(false); setEditEvent(null); } };
    window.addEventListener("deskte:nav-reset",h);
    return ()=>window.removeEventListener("deskte:nav-reset",h);
  },[]);

  const MONTHS = lang==="es"
    ?["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"]
    :["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS = lang==="es"?["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"]:["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const EVENT_TYPES = [
    { id:"appointment", label:lang==="es"?"Cita":"Appointment", color:"#00d4ff", icon:"📅" },
    { id:"followup", label:lang==="es"?"Seguimiento":"Follow Up", color:"#ffb700", icon:"🔔" },
    { id:"renewal", label:lang==="es"?"Renovación":"Renewal", color:"#f97316", icon:"🔄" },
    { id:"call", label:lang==="es"?"Llamada":"Call", color:"#a78bfa", icon:"📞" },
    { id:"meeting", label:lang==="es"?"Reunión":"Meeting", color:"#10b981", icon:"🤝" },
    { id:"deadline", label:lang==="es"?"Fecha Límite":"Deadline", color:"#ef4444", icon:"⏰" },
    { id:"birthday", label:lang==="es"?"Cumpleaños":"Birthday", color:"#ec4899", icon:"🎂" },
    { id:"busy", label:lang==="es"?"Ocupado":"Busy", color:"#94a3b8", icon:"🔒" },
    { id:"other", label:lang==="es"?"Otro":"Other", color:"#64748b", icon:"📌" },
  ];
  const getEventType = (id) => EVENT_TYPES.find(e=>e.id===id)||EVENT_TYPES[0];
  // External personal-calendar mirror events are read-only (one-way mirror of
  // Google/Outlook) — no edit/delete, and the title is masked to "Ocupado"
  // unless the viewer is allowed to see the real one (backend sets masked).
  const isExternal = (ev) => ev?.source === "external_cal" || ev?.type === "busy";
  const evTitle = (ev) => (ev?.masked ? (lang === "es" ? "Ocupado" : "Busy") : (ev?.title || ""));

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i=firstDay-1; i>=0; i--) cells.push({ day:prevMonthDays-i, current:false, date:null });
  for (let d=1; d<=daysInMonth; d++) {
    const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    cells.push({ day:d, current:true, date:dateStr });
  }
  const remaining = 42 - cells.length;
  for (let d=1; d<=remaining; d++) cells.push({ day:d, current:false, date:null });

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;

  // Auto-generate birthday events from clients
  const birthdayEvents = useMemo(()=>{
    return clients.filter(c=>c.dob).map(c=>{
      const parts=c.dob.split("-");
      if(parts.length<3) return null;
      const bDate = `${year}-${parts[1]}-${parts[2]}`;
      return { id:"bday_"+c.id+"_"+year, date:bDate, title:`🎂 ${c.firstName} ${c.lastName}`, type:"birthday", clientId:c.id, time:"", notes:lang==="es"?"Cumpleaños":"Birthday", auto:true };
    }).filter(Boolean);
  },[clients, year, lang]);

  // La dueña de una AGENCIA ve aquí solo los eventos de la agencia (sin
  // cliente). Pero en una cuenta de DOS AGENTES la "dueña" es la agente admin,
  // y con ese filtro Nina no veía ni una cita de Josef (Lissette 2026-10-07:
  // "todos ven lo mismo, no hay diferencia"). Ahí ve todo, igual que él.
  const isAgencyOwner = ["agency_owner","agency_admin"].includes(currentUser?.role) && !esCuentaDeDosAgentes(users);
  // TODOS VEN TODO (E-421). Aquí la dueña y la agente admin veían SOLO los
  // eventos sin cliente: las citas con cliente —entre ellas todas las del
  // enlace de reservas— no les salían. Lissette 2026-11-03: "quiero que todos
  // puedan ver todas las citas de todos". `isAgencyOwner` sigue sirviendo
  // para el formulario (bloqueos para otros agentes).
  const allEvents = [...events, ...birthdayEvents];
  // QUÉ TIPOS SE ENSEÑAN (Lissette 2026-10-17: *"en el calendario queremos
  // tener la opción de que se muestre y que no, por ejemplo los cumpleaños"*).
  // Es una preferencia de quien mira, no de la agencia: se guarda en este
  // navegador. Si el navegador no deja guardar, se enseña todo, como siempre.
  const [ocultos, setOcultos] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('deskte.calendario.ocultos') || '[]')); }
    catch { return new Set(); }
  });
  const alternarTipo = (id) => setOcultos(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    try { localStorage.setItem('deskte.calendario.ocultos', JSON.stringify([...n])); }
    catch { /* sin almacenamiento: el filtro vale solo para esta visita */ }
    return n;
  });
  const visibles = allEvents.filter(e => !ocultos.has(e.type || 'appointment'));
  const getDateEvents = (dateStr) => visibles.filter(e=>e.date===dateStr);

  const prevMonth = () => { if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
  const nextMonth = () => { if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

  // Fechas en día LOCAL (nunca toISOString: en Florida corre el día).
  const aTexto = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const deTexto = (t) => { const [a,m,d] = String(t).split("-").map(Number); return new Date(a, m-1, d); };
  // Mover el día de referencia también mueve el mes (los cumpleaños salen del año a la vista).
  const setAncla = (t) => { setAnclaRaw(t); const d = deTexto(t); setYear(d.getFullYear()); setMonth(d.getMonth()); };
  const moverAncla = (dias) => { const d = deTexto(ancla); d.setDate(d.getDate()+dias); setAncla(aTexto(d)); };
  const diasDeLaSemana = (() => {
    const ini = deTexto(ancla); ini.setDate(ini.getDate() - ini.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(ini); d.setDate(ini.getDate()+i); return aTexto(d); });
  })();
  const anterior = () => viewMode === "month" ? prevMonth() : moverAncla(viewMode === "week" ? -7 : -1);
  const siguiente = () => viewMode === "month" ? nextMonth() : moverAncla(viewMode === "week" ? 7 : 1);
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); setAnclaRaw(todayStr); };
  const tituloDeLaVista = (() => {
    if (viewMode === "month") return `${MONTHS[month]} ${year}`;
    const loc = lang === "es" ? "es-US" : "en-US";
    if (viewMode === "day") return deTexto(ancla).toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const a = deTexto(diasDeLaSemana[0]); const b = deTexto(diasDeLaSemana[6]);
    const corto = (d) => d.toLocaleDateString(loc, { day: "numeric", month: "short" });
    return `${corto(a)} – ${corto(b)} ${b.getFullYear()}`;
  })();
  const porHora = (a, b) => (a.time || "99:99").localeCompare(b.time || "99:99");

  const emptyEvent = { id:"", date:selectedDate||todayStr, title:"", type:"appointment", clientId:"", time:"", notes:"", durationMinutes:60 };

  const isApiModeC = typeof window !== "undefined" && !!window.__deskteAuth;
  // Re-pull the calendar from the server. Used to self-heal a stale local
  // event list — e.g. after switching accounts / "ver como agencia", the
  // cached events can belong to a different agency than the one the API
  // call now targets, so an edit/delete 404s with "Appointment not found"
  // even though the row is still on screen (Lissette 2026-06-16).
  const refreshEventsFromServer = async () => {
    if (!isApiModeC) return;
    try {
      const fresh = await apiListAppointments();
      onEventsChange(fresh.events);
    } catch { /* offline / transient — keep current state */ }
  };
  const saveEvent = async (ev) => {
    if (editEvent) {
      if (isApiModeC) {
        try {
          await apiUpdateAppointment(editEvent.id, ev);
        } catch (err) {
          const stale = /not found/i.test(err.message || "");
          if (stale) {
            await refreshEventsFromServer();
            toast.error(lang === "es"
              ? "Ese evento ya no existe en el servidor (pudo borrarse o ser de otra cuenta). Refresqué el calendario — vuelve a crearlo si hace falta."
              : "That event no longer exists on the server (it may have been deleted or belongs to another account). I refreshed the calendar — recreate it if needed.");
          } else {
            toast.error((lang === "es" ? "No se pudo actualizar el evento: " : "Failed to update event: ") + err.message);
          }
          return;
        }
      }
      // Si se pasó a otra persona, se dice a quién y el nombre queda al día.
      const pasada = ev.agentId && ev.agentId !== editEvent.agentId;
      const nuevoNombre = pasada ? ((users || []).find((u) => u.id === ev.agentId)?.name || ev.agentName) : ev.agentName;
      if (pasada) toast.success(lang === "es" ? `Cita pasada a ${nuevoNombre}.` : `Appointment handed to ${nuevoNombre}.`);
      onEventsChange(prev=>prev.map(e=>e.id===editEvent.id?{...ev,agentName:nuevoNombre,id:editEvent.id}:e));
    } else {
      if (isApiModeC) {
        try {
          const created = await apiCreateAppointment(ev);
          // Merge-or-push: dedup against the SSE 'appointment.created' echo
          // (feedback_sse_echo_dedup_optimistic_add).
          onEventsChange(prev=>prev.some(e=>e.id===created.id)
            ? prev.map(e=>e.id===created.id?{...e,...ev,...created}:e)
            : [...prev, { ...ev, ...created }]);
        } catch (err) {
          toast.error("Failed to save event: " + err.message);
          return;
        }
      } else {
        onEventsChange(prev=>[...prev,{...ev,id:"ev_"+Date.now()}]);
      }
    }
    setShowForm(false); setEditEvent(null);
  };
  const deleteEvent = async (id) => {
    // Only real backend appointments (API mode, non-synthetic id) need a
    // server call. Local fallback ids ("ev_...") and auto birthday events
    // ("bday_...") never reach here with a real id, so just drop them locally.
    if (isApiModeC && typeof id === "string" && !id.startsWith("ev_") && !id.startsWith("bday_")) {
      try {
        await apiDeleteAppointment(id);
      } catch (err) {
        const stale = /not found/i.test(err.message || "");
        if (stale) {
          // Already gone server-side — drop it locally and resync so the
          // ghost row disappears instead of erroring on every retry.
          onEventsChange(prev=>prev.filter(e=>e.id!==id));
          await refreshEventsFromServer();
          return;
        }
        toast.error((lang==="es"?"No se pudo eliminar el evento: ":"Failed to delete event: ") + err.message);
        return;
      }
    }
    onEventsChange(prev=>prev.filter(e=>e.id!==id));
  };

  // EventForm is defined at module scope (top of this file) so it keeps a stable
  // identity across re-renders — see the comment on that definition.

  // Day detail panel
  const dayEvents = selectedDate ? getDateEvents(selectedDate) : [];

  // Una cita como tarjeta: en el panel del día (vistas Mes y Semana) y en la vista Día.
  const tarjetaDeEvento = (ev) => {
                const et=getEventType(ev.type);
                const c=ev.clientId?clients.find(cl=>cl.id===ev.clientId):null;
                return(
                  <div key={ev.id} style={{background:"var(--c-surface,#0d1c2e)",border:`1px solid ${et.color}33`,borderLeft:`3px solid ${et.color}`,borderRadius:9,padding:".55rem .7rem"}}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
                      <div>
                        <div style={{fontWeight:700,fontSize:".78rem",color:"var(--c-white,#eef6ff)",display:"flex",alignItems:"center",gap:".3rem"}}>
                          <span>{et.icon}</span>{evTitle(ev)}
                        </div>
                        <div style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",marginTop:2,display:"flex",alignItems:"center",gap:".4rem",flexWrap:"wrap"}}>
                          {ev.time&&<span>🕐 {ev.time}</span>}
                          <span style={{fontSize:".6rem",padding:"1px 6px",borderRadius:5,background:et.color+"18",color:et.color,fontWeight:600}}>{et.label}</span>
                        </div>
                        {c&&<div style={{fontSize:".65rem",color:"var(--c-cyan,#00d4ff)",marginTop:3}}>👤 {c.firstName} {c.lastName}</div>}
                        {ev.policyId && (() => {
                          const days = daysUntilExp(ev.policyId);
                          if (days === null) return null;
                          const color = days < 0 ? '#ef4444' : days <= 7 ? '#ef4444' : days <= 30 ? '#f97316' : days <= 90 ? '#ffb700' : '#7aa3c0';
                          const label = days < 0
                            ? (lang === 'es' ? `Venció hace ${Math.abs(days)} días` : `${Math.abs(days)} days overdue`)
                            : (lang === 'es' ? `${days} días para vencer` : `${days} days to expire`);
                          return (
                            <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: color + '22', color, border: `1px solid ${color}55`, fontFamily: 'var(--font-mono)' }}>
                                ⏰ {label}
                              </span>
                              <button type="button" onClick={() => openPolicyClient(ev)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'rgba(0,212,255,.12)', color: 'var(--c-cyan,#00d4ff)', border: '1px solid rgba(0,212,255,.32)', cursor: 'pointer', fontFamily: 'inherit' }}>
                                <Shield size={9} /> {lang === 'es' ? 'Ver póliza' : 'View policy'} <ExternalLink size={9} />
                              </button>
                            </div>
                          );
                        })()}
                        {ev.notes&&<div style={{fontSize:".68rem",color:"var(--c-text2,#7aa3c0)",marginTop:3}}><RichNote text={ev.notes}/></div>}
                      </div>
                      {/* La cita de otro agente se VE pero no se cambia (E-421): sin lápiz ni papelera. */}
                      {!ev.auto&&!isExternal(ev)&&ev.editable===false&&ev.agentName&&<span style={{fontSize:".6rem",color:"var(--c-text3,#45688a)",flexShrink:0,alignSelf:"center"}}>👤 {ev.agentName}</span>}
                      {!ev.auto&&!isExternal(ev)&&ev.editable!==false&&<div style={{display:"flex",gap:".25rem",flexShrink:0}}>
                        <button type="button" onClick={()=>{setEditEvent(ev);setShowForm(true);}}
                          style={{background:"none",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text3,#45688a)",cursor:"pointer",padding:"3px 6px",borderRadius:6,lineHeight:0}}><Edit2 size={11}/></button>
                        <button type="button" onClick={async()=>{if(await confirmDialog({message:lang==="es"?"¿Eliminar?":"Delete?",danger:true}))deleteEvent(ev.id);}}
                          style={{background:"none",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text3,#45688a)",cursor:"pointer",padding:"3px 6px",borderRadius:6,lineHeight:0}}><Trash2 size={11}/></button>
                      </div>}
                    </div>
                  </div>
    );
  };

  return (
    <div>
      {/* HEADER */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1rem",flexWrap:"wrap",gap:".5rem"}}>
        <div style={{display:"flex",alignItems:"center",gap:".5rem"}}>
          <span style={{fontSize:"1.4rem"}}>📅</span>
          <h2 style={{fontSize:"1.1rem",fontWeight:800,color:"var(--c-white,#eef6ff)",margin:0}}>{lang==="es"?"Calendario":"Calendar"}</h2>
        </div>
        <div style={{display:"flex",gap:".35rem"}}>
          <button type="button" onClick={goToday}
            style={{padding:".35rem .7rem",borderRadius:8,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-cyan,#00d4ff)",fontSize:".72rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {lang==="es"?"Hoy":"Today"}
          </button>
          <button type="button"
            onClick={async()=>{
              try {
                const result = await apiGenerateRenewalReminders();
                const fresh = await apiListAppointments();
                onEventsChange(fresh.events);
                toast.success(lang==="es"
                  ? `${result.created} eventos de renovación creados (${result.skipped} omitidos)`
                  : `${result.created} renewal events created (${result.skipped} skipped)`);
              } catch(e) {
                toast.error(lang==="es" ? `Error: ${e.message}` : `Error: ${e.message}`);
              }
            }}
            title={lang==="es"?"Genera recordatorios automáticos para pólizas que vencen en 90/60/30/7 días":"Auto-generate reminders for policies expiring in 90/60/30/7 days"}
            style={{display:"flex",alignItems:"center",gap:5,padding:".35rem .7rem",borderRadius:8,border:"1px solid #f97316",background:"#f9731622",color:"#f97316",fontSize:".72rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            <RefreshCw size={11}/>{lang==="es"?"Gen. Renovaciones":"Gen. Renewals"}
          </button>
          <button type="button" onClick={()=>setShowExtCal(true)}
            title={lang==="es"?"Conecta tu Google/Outlook para que se vean tus eventos personales como \"Ocupado\"":"Connect your Google/Outlook so your personal events show as \"Busy\""}
            style={{display:"flex",alignItems:"center",gap:5,padding:".35rem .7rem",borderRadius:8,border:"1px solid #10b981",background:"#10b98122",color:"#10b981",fontSize:".72rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            <Calendar size={11}/>{lang==="es"?"Mi calendario":"My calendar"}
          </button>
          <button type="button" onClick={()=>{setEditEvent(null);setShowForm(true);}}
            style={{display:"flex",alignItems:"center",gap:5,background:"linear-gradient(135deg,#0099cc,#0055aa)",border:"none",color:"#fff",fontSize:".78rem",fontWeight:700,padding:".5rem 1rem",borderRadius:9,cursor:"pointer",fontFamily:"inherit"}}>
            <Plus size={14}/>{lang==="es"?"Nuevo Evento":"New Event"}
          </button>
        </div>
      </div>

      {/* MOSTRAR / OCULTAR POR TIPO */}
      <div style={{display:"flex",alignItems:"center",gap:".35rem",flexWrap:"wrap",marginBottom:".75rem"}}>
        <span style={{fontSize:".68rem",fontWeight:700,color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".06em",marginRight:2}}>{lang==="es"?"Mostrar:":"Show:"}</span>
        {EVENT_TYPES.map(et=>{ const on=!ocultos.has(et.id); return (
          <button key={et.id} type="button" onClick={()=>alternarTipo(et.id)} aria-pressed={on}
            title={on?(lang==="es"?`Ocultar ${et.label}`:`Hide ${et.label}`):(lang==="es"?`Mostrar ${et.label}`:`Show ${et.label}`)}
            style={{display:"inline-flex",alignItems:"center",gap:4,padding:".22rem .6rem",borderRadius:100,fontSize:".7rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit",
              border:`1px solid ${on?et.color:"var(--c-border,#1a3550)"}`,background:on?`${et.color}22`:"transparent",color:on?et.color:"var(--c-text3,#45688a)",textDecoration:on?"none":"line-through",opacity:on?1:.75}}>
            <span aria-hidden="true">{et.icon}</span>{et.label}
          </button>); })}
        {ocultos.size>0&&<button type="button" onClick={()=>{ setOcultos(new Set()); try{ localStorage.removeItem('deskte.calendario.ocultos'); }catch{ /* sin almacenamiento */ } }}
          style={{background:"none",border:"none",color:"var(--c-cyan,#00d4ff)",fontSize:".7rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{lang==="es"?"Mostrar todo":"Show all"}</button>}
      </div>

      {/* NAVEGACIÓN + MES / SEMANA / DÍA */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"1rem",marginBottom:".9rem",flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:"1rem"}}>
          <button type="button" onClick={anterior} aria-label={lang==="es"?"Anterior":"Previous"} style={{background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text2,#7aa3c0)",cursor:"pointer",padding:"8px 14px",borderRadius:8,fontSize:".9rem",fontWeight:700,fontFamily:"inherit"}}>◀</button>
          <div style={{fontWeight:800,fontSize:"1.25rem",color:"var(--c-white,#eef6ff)",minWidth:220,textAlign:"center",textTransform:"capitalize"}}>{tituloDeLaVista}</div>
          <button type="button" onClick={siguiente} aria-label={lang==="es"?"Siguiente":"Next"} style={{background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text2,#7aa3c0)",cursor:"pointer",padding:"8px 14px",borderRadius:8,fontSize:".9rem",fontWeight:700,fontFamily:"inherit"}}>▶</button>
        </div>
        <div role="group" aria-label={lang==="es"?"Vista":"View"} style={{display:"flex",border:"1px solid var(--c-border,#1a3550)",borderRadius:9,overflow:"hidden"}}>
          {[["month",lang==="es"?"Mes":"Month"],["week",lang==="es"?"Semana":"Week"],["day",lang==="es"?"Día":"Day"]].map(([id,label])=>(
            <button key={id} type="button" aria-pressed={viewMode===id}
              onClick={()=>{ if(id!=="month"&&viewMode==="month"){ setAnclaRaw(selectedDate || (year===today.getFullYear()&&month===today.getMonth() ? todayStr : aTexto(new Date(year,month,1)))); } setViewMode(id); if(id==="day") setSelectedDate(null); }}
              style={{padding:".45rem 1rem",border:"none",fontSize:".8rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                background:viewMode===id?"var(--c-cyan-dim,rgba(0,212,255,.12))":"transparent",color:viewMode===id?"var(--c-cyan,#00d4ff)":"var(--c-text2,#7aa3c0)"}}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:selectedDate&&viewMode!=="day"?"1fr 320px":"1fr",gap:"1rem",alignItems:"start"}}>
        {/* CALENDAR GRID */}
        {viewMode==="month"&&<div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,marginBottom:1}}>
            {DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:".75rem",fontWeight:700,color:"var(--c-text3,#45688a)",padding:".45rem 0",textTransform:"uppercase",letterSpacing:".08em",fontFamily:"var(--font-mono)"}}>{d}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {cells.map((cell,i)=>{
              const evts = cell.date ? getDateEvents(cell.date) : [];
              const isToday = cell.date === todayStr;
              const isSelected = cell.date === selectedDate;
              return(
                <button type="button" key={i} onClick={()=>cell.date&&setSelectedDate(cell.date===selectedDate?null:cell.date)}
                  disabled={!cell.current}
                  style={{
                    minHeight:118, padding:".4rem", borderRadius:8, border:`1px solid ${isSelected?"var(--c-cyan,#00d4ff)":isToday?"rgba(0,212,255,.3)":"var(--c-border,#1a3550)"}`,
                    background:isSelected?"var(--c-cyan-dim)":isToday?"rgba(0,212,255,.05)":"var(--c-surface2,#112538)",
                    cursor:cell.current?"pointer":"default", opacity:cell.current?1:.35, fontFamily:"inherit",
                    display:"flex",flexDirection:"column",alignItems:"stretch",transition:"all .12s",textAlign:"left",
                  }}>
                  <div style={{fontSize:".88rem",fontWeight:isToday?800:600,color:isToday?"var(--c-cyan,#00d4ff)":"var(--c-white,#eef6ff)",marginBottom:3,textAlign:"right",paddingRight:2}}>
                    {cell.day}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:2,flex:1}}>
                    {[...evts].sort(porHora).slice(0,4).map(ev=>{
                      const et=getEventType(ev.type);
                      const _t=evTitle(ev);
                      return <div key={ev.id} title={_t} style={{fontSize:".68rem",fontWeight:600,color:et.color,background:et.color+"18",borderRadius:4,padding:"2px 5px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.4}}>
                        {ev.time?`${ev.time} `:""}{et.icon} {_t}
                      </div>;
                    })}
                    {evts.length>4&&<div style={{fontSize:".62rem",color:"var(--c-text3,#45688a)",textAlign:"center"}}>+{evts.length-4}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>}

        {/* SEMANA: los 7 días en columnas, con las citas enteras y su hora. */}
        {viewMode==="week"&&<div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:4}}>
          {diasDeLaSemana.map((dia,i)=>{
            const evts=[...getDateEvents(dia)].sort(porHora);
            const isToday=dia===todayStr; const isSelected=dia===selectedDate;
            return(
              <div key={dia} style={{display:"flex",flexDirection:"column",minHeight:440,border:`1px solid ${isSelected?"var(--c-cyan,#00d4ff)":isToday?"rgba(0,212,255,.3)":"var(--c-border,#1a3550)"}`,borderRadius:10,background:isToday?"rgba(0,212,255,.05)":"var(--c-surface2,#112538)",overflow:"hidden"}}>
                <button type="button" onClick={()=>{setAnclaRaw(dia);setViewMode("day");setSelectedDate(null);}}
                  title={lang==="es"?"Ver este día":"View this day"}
                  style={{background:"none",border:"none",borderBottom:"1px solid var(--c-border,#1a3550)",padding:".5rem .3rem",cursor:"pointer",fontFamily:"inherit",textAlign:"center"}}>
                  <div style={{fontSize:".7rem",fontWeight:700,color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".08em"}}>{DAYS[i]}</div>
                  <div style={{fontSize:"1.25rem",fontWeight:800,color:isToday?"var(--c-cyan,#00d4ff)":"var(--c-white,#eef6ff)"}}>{deTexto(dia).getDate()}</div>
                </button>
                <div style={{display:"flex",flexDirection:"column",gap:4,padding:".4rem",flex:1}}>
                  {evts.length===0&&<div style={{fontSize:".7rem",color:"var(--c-text3,#45688a)",textAlign:"center",marginTop:".5rem"}}>—</div>}
                  {evts.map(ev=>{
                    const et=getEventType(ev.type);
                    return(
                      <button type="button" key={ev.id} onClick={()=>setSelectedDate(dia)}
                        style={{textAlign:"left",fontFamily:"inherit",cursor:"pointer",background:et.color+"18",border:`1px solid ${et.color}44`,borderLeft:`3px solid ${et.color}`,borderRadius:6,padding:".35rem .45rem",color:"var(--c-white,#eef6ff)"}}>
                        {ev.time&&<div style={{fontSize:".7rem",fontWeight:800,color:et.color}}>{ev.time}</div>}
                        <div style={{fontSize:".76rem",fontWeight:600,lineHeight:1.3,wordBreak:"break-word"}}>{et.icon} {evTitle(ev)}</div>
                        {ev.agentName&&<div style={{fontSize:".64rem",color:"var(--c-text3,#45688a)",marginTop:2}}>👤 {ev.agentName}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>}

        {/* DÍA: las citas del día como tarjetas grandes, con sus botones. */}
        {viewMode==="day"&&<div style={{background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:12,padding:"1.1rem",minHeight:420}}>
          <button type="button" onClick={()=>{setSelectedDate(ancla);setEditEvent(null);setShowForm(true);}}
            style={{width:"100%",marginBottom:".8rem",display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:"var(--c-surface3,#162d44)",border:"1px dashed var(--c-border,#1a3550)",color:"var(--c-cyan,#00d4ff)",cursor:"pointer",padding:".6rem",borderRadius:9,fontSize:".85rem",fontWeight:700,fontFamily:"inherit"}}>
            <Plus size={15}/>{lang==="es"?"Agregar evento este día":"Add event on this day"}
          </button>
          {getDateEvents(ancla).length===0&&<div style={{textAlign:"center",padding:"2rem",color:"var(--c-text3,#45688a)",fontSize:".9rem"}}>{lang==="es"?"Sin eventos este día":"No events this day"}</div>}
          <div style={{display:"flex",flexDirection:"column",gap:".55rem",zoom:1.3}}>
            {[...getDateEvents(ancla)].sort(porHora).map(tarjetaDeEvento)}
          </div>
        </div>}

        {/* DAY DETAIL PANEL */}
        {selectedDate&&(
          <div style={{background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:12,padding:"1rem",position:"sticky",top:10}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:".75rem"}}>
              <div style={{fontWeight:800,fontSize:".92rem",color:"var(--c-white,#eef6ff)"}}>{fmt(selectedDate)}</div>
              <button type="button" onClick={()=>setSelectedDate(null)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)"}}><X size={16}/></button>
            </div>
            <button type="button" onClick={()=>{setEditEvent(null);setShowForm(true);}}
              style={{width:"100%",marginBottom:".65rem",display:"flex",alignItems:"center",justifyContent:"center",gap:5,background:"var(--c-surface3,#162d44)",border:"1px dashed var(--c-border,#1a3550)",color:"var(--c-cyan,#00d4ff)",cursor:"pointer",padding:".4rem",borderRadius:8,fontSize:".72rem",fontWeight:600,fontFamily:"inherit"}}>
              <Plus size={13}/>{lang==="es"?"Agregar evento":"Add event"}
            </button>
            {dayEvents.length===0&&<div style={{textAlign:"center",padding:"1rem",color:"var(--c-text3,#45688a)",fontSize:".78rem"}}>{lang==="es"?"Sin eventos":"No events"}</div>}
            <div style={{display:"flex",flexDirection:"column",gap:".4rem"}}>
              {[...dayEvents].sort(porHora).map(tarjetaDeEvento)}
            </div>
          </div>
        )}
      </div>

      {/* Upcoming events summary */}
      {!selectedDate && (()=>{
        const upcoming = visibles.filter(e=>e.date>=todayStr).sort((a,b)=>a.date.localeCompare(b.date)||((a.time||"").localeCompare(b.time||""))).slice(0,8);
        if(upcoming.length===0) return null;
        return (
          <div style={{marginTop:"1rem"}}>
            <div style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:".5rem",fontFamily:"var(--font-mono)"}}>📋 {lang==="es"?"Próximos Eventos":"Upcoming Events"}</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:".4rem"}}>
              {upcoming.map(ev=>{
                const et=getEventType(ev.type);
                const c=ev.clientId?clients.find(cl=>cl.id===ev.clientId):null;
                const days = ev.policyId ? daysUntilExp(ev.policyId) : null;
                const dColor = days === null ? null : days < 0 ? '#ef4444' : days <= 7 ? '#ef4444' : days <= 30 ? '#f97316' : days <= 90 ? '#ffb700' : '#7aa3c0';
                return(
                  <button type="button" key={ev.id}
                    onClick={()=>{
                      if (ev.policyId) { openPolicyClient(ev); return; }
                      setSelectedDate(ev.date); const parts=ev.date.split("-"); setYear(+parts[0]); setMonth(+parts[1]-1);
                    }}
                    title={ev.policyId ? (lang === 'es' ? 'Click para abrir la póliza' : 'Click to open the policy') : ''}
                    style={{background:"var(--c-surface2,#112538)",border:`1px solid ${et.color}33`,borderLeft:`3px solid ${et.color}`,borderRadius:9,padding:".5rem .7rem",cursor:"pointer",textAlign:"left",fontFamily:"inherit",transition:"all .12s",display:"flex",alignItems:"center",gap:".6rem"}}>
                    <div style={{fontSize:"1.1rem"}}>{et.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:".75rem",color:"var(--c-white,#eef6ff)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{evTitle(ev)}</div>
                      <div style={{fontSize:".62rem",color:"var(--c-text3,#45688a)",display:"flex",gap:".4rem",marginTop:1,flexWrap:"wrap"}}>
                        <span>{fmt(ev.date)}</span>{ev.time&&<span>🕐 {ev.time}</span>}{c&&<span style={{color:"var(--c-cyan,#00d4ff)"}}>👤 {c.firstName}</span>}
                        {days !== null && <span style={{color: dColor, fontWeight:700}}>⏰ {days < 0 ? `${Math.abs(days)}d ${lang==='es'?'vencido':'overdue'}` : `${days}d`}</span>}
                        {resumenDelBloqueo(ev, users, lang) && <span style={{color:"var(--c-amber,#ffb700)"}}>🔒 {resumenDelBloqueo(ev, users, lang)}</span>}
                      </div>
                    </div>
                    {ev.policyId && <ExternalLink size={11} style={{color:'var(--c-text3,#45688a)',flexShrink:0}}/>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {showForm&&<EventForm initial={editEvent||{...emptyEvent,date:selectedDate||todayStr}} onSave={saveEvent} onCancel={()=>{setShowForm(false);setEditEvent(null);}}
        isEdit={!!editEvent} lang={lang} emptyEvent={emptyEvent} isAgencyOwner={isAgencyOwner} clients={clients} EVENT_TYPES={EVENT_TYPES} users={users}/>}

      {showExtCal&&<ExternalCalModal lang={lang} onClose={()=>setShowExtCal(false)} onSynced={refreshEventsFromServer}/>}
    </div>
  );
}
